import { useMemo } from 'react';

import { findAttachedGroupEvaluation } from './evaluationSelection';

import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';

import {
    useCreateCycleEvaluationForGroup,
    useGetCycleEvaluation,
    useUpdateCycleEvaluation,
} from '@/api/cycle-evaluation-services/cycle-evaluation';
import { useGetCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group';
import { useUpdateCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import { useImportTemplateInCycleQuestionnaire } from '@/api/cycle-evaluation-services/cycle-questionnaire';
import {
    useAmelioReviewTemplates,
    useReviewTemplates,
} from '@/api/review-services/review-template';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';

const TEMPLATE_PAGE_SIZE = 100;

export const useEvaluationElementsData = (cycleEvaluationGroupId?: string) => {
    const {
        data: group,
        isError: groupError,
        isPending: isGroupPending,
    } = useGetCycleEvaluationGroup(cycleEvaluationGroupId);

    const managerTemplates = useReviewTemplates({
        pageSize: TEMPLATE_PAGE_SIZE,
        meta: { reviewType: EvaluationType.MANAGER_EVALUATION },
    });
    const feedback360Templates = useReviewTemplates({
        pageSize: TEMPLATE_PAGE_SIZE,
        meta: { reviewType: EvaluationType.EVALUATION_360 },
    });
    const selfAssessmentTemplates = useReviewTemplates({
        pageSize: TEMPLATE_PAGE_SIZE,
        meta: { reviewType: EvaluationType.SELF_EVALUATION },
    });

    const amelioManagerTemplates = useAmelioReviewTemplates(EvaluationType.MANAGER_EVALUATION);
    const amelioFeedback360Templates = useAmelioReviewTemplates(EvaluationType.EVALUATION_360);
    const amelioSelfAssessmentTemplates = useAmelioReviewTemplates(EvaluationType.SELF_EVALUATION);

    const managerEvaluationChild = findAttachedGroupEvaluation(
        group,
        EvaluationType.MANAGER_EVALUATION,
    );
    const feedback360Child = findAttachedGroupEvaluation(group, EvaluationType.EVALUATION_360);
    const selfAssessmentChild = findAttachedGroupEvaluation(group, EvaluationType.SELF_EVALUATION);

    const managerEvaluationQuery = useGetCycleEvaluation(managerEvaluationChild?.id ?? '');
    const feedback360Query = useGetCycleEvaluation(feedback360Child?.id ?? '');
    const selfAssessmentQuery = useGetCycleEvaluation(selfAssessmentChild?.id ?? '');

    const cycleEvaluationByType: Partial<Record<EvaluationTypeValue, CycleEvaluation>> = useMemo(
        () => ({
            [EvaluationType.MANAGER_EVALUATION]: managerEvaluationQuery.data,
            [EvaluationType.EVALUATION_360]: feedback360Query.data,
            [EvaluationType.SELF_EVALUATION]: selfAssessmentQuery.data,
        }),
        [managerEvaluationQuery.data, feedback360Query.data, selfAssessmentQuery.data],
    );

    const { mutateAsync: updateCycleEvaluationGroup } = useUpdateCycleEvaluationGroup();
    const { mutateAsync: createCycleEvaluationForGroup } = useCreateCycleEvaluationForGroup();
    const { mutateAsync: updateCycleEvaluation } = useUpdateCycleEvaluation();
    const { mutateAsync: importTemplateInCycleQuestionnaire } =
        useImportTemplateInCycleQuestionnaire();

    useFetchErrorsHandler({
        errors: [
            groupError,
            managerTemplates.isError,
            feedback360Templates.isError,
            selfAssessmentTemplates.isError,
            amelioManagerTemplates.isError,
            amelioFeedback360Templates.isError,
            amelioSelfAssessmentTemplates.isError,
            managerEvaluationQuery.isError,
            feedback360Query.isError,
            selfAssessmentQuery.isError,
        ],
    });

    const isLoading =
        isGroupPending ||
        managerTemplates.isLoading ||
        feedback360Templates.isLoading ||
        selfAssessmentTemplates.isLoading ||
        amelioManagerTemplates.isLoading ||
        amelioFeedback360Templates.isLoading ||
        amelioSelfAssessmentTemplates.isLoading ||
        (!!managerEvaluationChild && managerEvaluationQuery.isPending) ||
        (!!feedback360Child && feedback360Query.isPending) ||
        (!!selfAssessmentChild && selfAssessmentQuery.isPending);

    return {
        group,
        managerTemplates,
        feedback360Templates,
        selfAssessmentTemplates,
        amelioManagerTemplates,
        amelioFeedback360Templates,
        amelioSelfAssessmentTemplates,
        cycleEvaluationByType,
        updateCycleEvaluationGroup,
        createCycleEvaluationForGroup,
        updateCycleEvaluation,
        importTemplateInCycleQuestionnaire,
        isLoading,
    };
};
